import isEqual from 'react-fast-compare';
import { createStore, createEvent, combine, restore } from 'effector';
import { throttle } from 'patronum/throttle';
import { signout } from '@features/common';
import { createForm } from '@unicorn/effector-form';
import type { Config, SearchForm } from './table.types';
import { DataGridColumns, DataGridInternalColumn, ExternalOrderedColumns, ExternalResizedColumns } from '@unicorn-shared/interface';

/*
  Можно сохранять количество строк в таблице ($pageSize), ширину и порядок столбцов ($columns),
  видимость столбцов ($visibilityColumns).
  Пример реализации сохранения можно посмотреть в фиче polls. Файлы:
    1) модель сохранения /features/polls/models/user-settings/user-settings.model,
    2) обработка полученных значений с бэка/features/polls/models/table/table.init,
    3) запрос сохраненных данных /features/polls/models/page/page.init
*/

const getConfig = <T>(config: Config<T>) => ({
  search: '',
  sorting: [],
  visibilityColumns: {},
  selectRow: [],
  pinnedColumns: { left: [], right: [] },
  paginationModel: { page: 0, pageSize: 10 },
  ...config,
});

export const createTableBag = <T>(customConfig: Config<T>) => {
  const config = getConfig(customConfig);

  const searchForm = createForm<SearchForm>({
    initialValues: {
      search: config.search,
    },
    editable: true,
  });

  const sortingReadyChanged = createEvent<boolean>();
  const paginationModelChanged = createEvent<{ pageSize: number; page: number; }>();
  const sortChanged = createEvent<{ field: string; sort: 'asc' | 'desc' | null | undefined }[]>();
  const searchChanged = createEvent<string>();
  const selectionRowChanged = createEvent<(string | number)[]>();
  const visibilityColumnsChanged = createEvent<Record<string, boolean>>();
  const columnsWidthChanged = createEvent<ExternalResizedColumns>();
  const orderColumnsChanged = createEvent<ExternalOrderedColumns>();
  const resetOrderedColumns = createEvent<void>();
  const resetResizedColumns = createEvent<void>();
  const openRowChanged = createEvent<string | number | null>();
  const changePinnedColumns = createEvent<{ left?: string[]; right?: string[] }>();
  const mergeColumnsByPattern = createEvent<DataGridColumns<T>>();

  const throttledSearch = throttle({
    source: searchChanged,
    timeout: 1000,
  });

  const $pagination = createStore<{ pageSize: number; page: number; }>({
    page: config.paginationModel.page,
    pageSize: config.paginationModel.pageSize,
  });

  const $sorting = createStore<{ field: string; sort: 'asc' | 'desc' | null | undefined }[]>(config.sorting);
  const $search = createStore<string>(config.search);
  const $columns = createStore<DataGridColumns<T>>(config.columns);
  const $selectRow = createStore<(string | number)[]>(config.selectRow);

  const $columnWidths = $columns.map((columns) =>
    columns.map(({ field, width }) => ({ field, width }))
  );

  const $columnOrders = $columns.map((columns) => columns.map(({ field }) => field));

  const $openRow = createStore<string | number | null>(config?.openRow ?? null);
  const $pinnedColumns = createStore<{ left?: string[]; right?: string[] }>(config.pinnedColumns);

  /*
    $selectRow хранит какая строка в таблице выбрана.
    В каждой фиче, где используется DataGrid:
    1) в модели дополнительно сбрасывать
    $selectRow при срабатывании ивентов detailClosed, addClicked,
    если используется фабрика деталей.
    2) после успешного выполнения запроса на создание сущности (fxCreate.done),
      из result взять id и передать в стор $selectRow в модели фичи
      (нужно чтобы после создания сущности в таблице отображалась выбранная строка)

    Пример можно посмотреть в /features/polls/models/table/table.init
  */
  const $visibilityColumns = createStore<Record<string, boolean>>({
    ...config.visibilityColumns,
  });
  /*
    $visibilityColumns представляет из себя объект.
    Структура объекта: название колонки и значение видимости.
    Если нет необходимости сохранять скрытые столбцы при переходе между разделами или
    сохранять на бэке, то можно не использовать

    Пример можно посмотреть в /features/polls/organisms/table/index.tsx
  */
  const $tableParams = combine(
    $pagination,
    $sorting,
    restore(throttledSearch, ''),
    ({ page, pageSize }, sorting, search) => ({
      page: Number(page) + 1,
      per_page: pageSize,
      sorting: sorting.map((item) => ({ field: item.field, sort: item.sort })),
      search,
    })
  );

  const $sortReady = createStore(false);
  $sortReady.on(sortingReadyChanged, (_, value) => value);

  $pagination
    .on(paginationModelChanged, (paginationModel, newPaginationModel) => {
      if (paginationModel.pageSize === newPaginationModel.pageSize) {
        if (paginationModel.page !== newPaginationModel.page) {
          return newPaginationModel;
        }

        return paginationModel;
      }

      return {
        ...newPaginationModel,
        page: 0,
      };
    })
    .on(throttledSearch, (paginationModel) => ({
      ...paginationModel,
      page: 0,
    }))
    .reset([signout]);

  $sorting.on(sortChanged, (prev, sort) => (isEqual(prev, sort) ? prev : sort)).reset(signout);
  $search.on(throttledSearch, (_, search) => search).reset(signout);
  $openRow.on(openRowChanged, (_, row) => row).reset(signout);
  $selectRow.on(selectionRowChanged, (_, row) => row).reset(signout);
  $visibilityColumns.on(visibilityColumnsChanged, (_, column) => column).reset(signout);

  $columnWidths
    .on(columnsWidthChanged, (state, { colDef }) => ({
      ...state,
      [colDef.field]: colDef.width,
    }))
    .reset(signout);

  $columnOrders
    .on(orderColumnsChanged, (columns, { oldIndex, targetIndex }) => {
      const orderedColumn = columns[oldIndex];
      return columns.toSpliced(oldIndex, 1).toSpliced(targetIndex, 0, orderedColumn);
    })
    .reset(signout);

  $columns
    .on(columnsWidthChanged, (state, { colDef: { width, field } }) =>
      state.map((item) =>
        item.field === field ? { ...item, width: width || item.width } : item
      )
    )
    .on(orderColumnsChanged, (columns, { oldIndex, targetIndex }) => {
      const orderedColumn = columns[oldIndex];
      return columns.toSpliced(oldIndex, 1).toSpliced(targetIndex, 0, orderedColumn);
    })
    .on(resetOrderedColumns, (columns) =>
      config.columns.map((column) => ({
        ...column,
        width: columns.find(({ field }) => field === column.field)?.width ?? column.width,
      }))
    )
    .on(resetResizedColumns, (columns) =>
      columns.map((column) => {
        const configColumn = config.columns.find(({ field }) => field === column.field);
        return {
          ...column,
          width: configColumn?.width,
        };
      })
    )
    .on(
      mergeColumnsByPattern,
      (columns: DataGridColumns<T>, storageColumns: DataGridColumns<T>): DataGridColumns<T> => {
        /* Фильтрую полученные колонки по признаку существовании их в конфиге */
        const relevantStorageColumns: DataGridColumns<T> = storageColumns.filter(
          ({ field }) => columns.some((column) => column.field === field)
        );

        /* новые колонки, которых еще нет в сторадже */
        const newColumns: DataGridColumns<T> = columns.filter(
          ({ field }) => !relevantStorageColumns.some((column) => column.field === field)
        );

        return relevantStorageColumns
          .map(({ field, width }) => {
            const column = columns.find((colDef) => colDef.field === field);
            return {
              ...column,
              field,
              width,
            } as DataGridInternalColumn<T>;
          })
          .concat(newColumns);
      }
    )
    .reset(signout);

  $pinnedColumns.on(changePinnedColumns, (_, columns) => columns).reset(signout);

  /** При изменении в инпуте поиска обновляется стор $search */
  throttle({
    source: searchForm.values,
    timeout: 1000,
    target: throttledSearch.prepend(({ search }: { search: string }) => search),
  });

  return {
    $columns,
    $pagination,
    setPagination: paginationModelChanged,
    paginationModelChanged,
    $sorting,
    setSorting: sortChanged,
    sortChanged,
    $visibilityColumns,
    setHiddenColumns: visibilityColumnsChanged,
    visibilityColumnsChanged,
    $pinnedColumns,
    setPinnedColumns: changePinnedColumns,
    changePinnedColumns,
    $orderedColumns: $columns.map((columns) => columns.map(({ field }) => field)),
    setOrderedColumns: orderColumnsChanged,
    resetOrderedColumns,
    orderColumnsChanged,
    $resizedColumns: $columns.map((columns) =>
      Object.fromEntries(
        columns.reduce((acc: [string, number][], { field, width }) => {
          const configWidth = config.columns.find(
            (column) => column.field === field
          )?.width;
          if (width && width !== configWidth) acc.push([field, width]);
          return acc;
        }, [])
      )
    ),
    setResizedColumns: columnsWidthChanged,
    resetResizedColumns,
    columnsWidthChanged,
    $selectedRows: $selectRow.map((rows) => rows.map(String)),
    $selectRow,
    setSelectedRows: selectionRowChanged,
    selectionRowChanged,
    $openRow,
    $currentRow: $openRow.map((row) => (row ? String(row) : '')),
    openRowChanged,
    $search,
    searchChanged,
    searchForm,
    $tableParams,
    mergeColumnsByPattern,
    $sortReady,
    sortingReadyChanged
  };
};
